import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Row, Col, Image, Spinner, Alert, Card, Table, Button } from 'react-bootstrap';
import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../services/api';

// Интерфейсы для данных
interface WBCardOption {
  name: string;
  value: string;
  charc_type?: number;
}

interface WBCardGroupedOption {
  group_name: string;
  options: WBCardOption[];
}

interface WBCardData {
  imt_id?: number;
  nm_id?: number;
  imt_name?: string;
  description?: string;
  options?: WBCardOption[];
  grouped_options?: WBCardGroupedOption[];
  media?: {
    photo_count?: number;
  };
  selling?: {
    brand_name?: string;
    supplier_id?: number;
  };
}

interface GraphDataPoint {
  date: string;
  value: number;
  formattedDate: string;
}

interface GraphsData {
  sales_graph: number[];
  stocks_graph: number[];
  price_graph: number[];
  collection_date: string | null;
}

interface ProductInfoModalProps {
  show: boolean;
  productId: string;
  imageUrl: string;
  onHide: () => void;
}

/**
 * Парсит URL изображения WB и возвращает базовый путь
 * Пример: https://basket-17.wbbasket.ru/vol2780/part278045/278045310/images/c246x328/1.webp
 * Возвращает: https://basket-17.wbbasket.ru/vol2780/part278045/278045310
 */
const parseWBImageUrl = (url: string): string | null => {
  if (!url) return null;
  
  // Паттерн для извлечения базового пути
  const match = url.match(/(https:\/\/basket-\d+\.wbbasket\.ru\/vol\d+\/part\d+\/\d+)/);
  return match ? match[1] : null;
};

/**
 * Генерирует URL для больших изображений
 */
const generateBigImageUrls = (baseUrl: string, count: number = 3): string[] => {
  const urls: string[] = [];
  for (let i = 1; i <= count; i++) {
    urls.push(`${baseUrl}/images/big/${i}.webp`);
  }
  return urls;
};

const ProductInfoModal: React.FC<ProductInfoModalProps> = ({ show, productId, imageUrl, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Данные карточки WB
  const [wbCardData, setWbCardData] = useState<WBCardData | null>(null);
  const [wbCardLoading, setWbCardLoading] = useState(false);
  const [wbCardError, setWbCardError] = useState<string | null>(null);
  
  // Данные графиков
  const [graphsData, setGraphsData] = useState<GraphsData | null>(null);
  const [graphsLoading, setGraphsLoading] = useState(false);
  const [graphsError, setGraphsError] = useState<string | null>(null);
  
  // Изображения
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [imageRetry, setImageRetry] = useState(0);
  const cardRequestRef = useRef(0);
  const graphsRequestRef = useRef(0);
  const imageGenRef = useRef(0);

  // Парсим базовый URL
  const baseUrl = useMemo(() => parseWBImageUrl(imageUrl), [imageUrl]);
  
  // Генерируем URL для больших изображений
  const bigImageUrls = useMemo(() => {
    if (!baseUrl) return [];
    // Используем количество фото из данных карточки или 3 по умолчанию
    const photoCount = wbCardData?.media?.photo_count || 3;
    return generateBigImageUrls(baseUrl, Math.min(photoCount, 10)); // Макс 10 изображений
  }, [baseUrl, wbCardData?.media?.photo_count]);

  const productKey = `${productId}|${baseUrl ?? ''}`;
  const [boundProductKey, setBoundProductKey] = useState(productKey);
  if (boundProductKey !== productKey) {
    setBoundProductKey(productKey);
    imageGenRef.current += 1;
    setImageErrors(new Set());
    setImageRetry(0);
    setSelectedImageIndex(0);
    setWbCardData(null);
  }

  const imageSrc = (url: string) => (imageRetry > 0 ? `${url}?r=${imageRetry}` : url);

  // Загружаем данные при открытии модального окна
  useEffect(() => {
    if (show && productId && baseUrl) {
      const cardRequestId = ++cardRequestRef.current;
      const graphsRequestId = ++graphsRequestRef.current;
      void loadData(cardRequestId, graphsRequestId);
    } else {
      cardRequestRef.current += 1;
      graphsRequestRef.current += 1;
      // Сбрасываем состояние при закрытии
      setWbCardData(null);
      setGraphsData(null);
      setError(null);
      setWbCardError(null);
      setGraphsError(null);
      setImageErrors(new Set());
      setSelectedImageIndex(0);
      setImageRetry(0);
    }
  }, [show, productId, baseUrl]);

  const loadData = async (cardRequestId: number, graphsRequestId: number) => {
    if (!baseUrl) {
      setError('Невозможно определить URL товара');
      return;
    }

    setLoading(true);
    setError(null);

    await Promise.all([
      loadWBCard(cardRequestId),
      loadGraphs(graphsRequestId)
    ]);

    setLoading(false);
  };

  const loadWBCard = async (requestId: number) => {
    if (!baseUrl) return;
    
    setWbCardLoading(true);
    setWbCardError(null);
    
    try {
      const response = await api.getWBCard(productId, baseUrl);
      if (requestId !== cardRequestRef.current) return;
      if (response.data.success) {
        setWbCardData(response.data.data);
      } else {
        setWbCardError(response.data.error || 'Ошибка загрузки данных карточки');
      }
    } catch (err: any) {
      if (requestId !== cardRequestRef.current) return;
      console.error('Error loading WB card:', err);
      setWbCardError(err.response?.data?.error || 'Не удалось загрузить данные карточки');
    } finally {
      if (requestId === cardRequestRef.current) setWbCardLoading(false);
    }
  };

  const loadGraphs = async (requestId: number) => {
    setGraphsLoading(true);
    setGraphsError(null);
    
    try {
      const response = await api.getProductGraphs(productId);
      if (requestId !== graphsRequestRef.current) return;
      if (response.data.success) {
        setGraphsData(response.data.data);
      } else {
        setGraphsError(response.data.error || 'Ошибка загрузки данных графиков');
      }
    } catch (err: any) {
      if (requestId !== graphsRequestRef.current) return;
      console.error('Error loading graphs:', err);
      setGraphsError(err.response?.data?.error || 'Не удалось загрузить данные графиков');
    } finally {
      if (requestId === graphsRequestRef.current) setGraphsLoading(false);
    }
  };

  const handleImageError = (index: number, generation: number) => {
    if (imageGenRef.current !== generation) return;
    setImageErrors(prev => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const reloadImages = () => {
    imageGenRef.current += 1;
    setImageErrors(new Set());
    setImageRetry(n => n + 1);
    if (!show || !baseUrl) return;
    const requestId = ++cardRequestRef.current;
    void loadWBCard(requestId);
  };

  /**
   * Подготовка данных для графиков
   * Рассчитываем даты на основе collection_date (конечная дата) и индекса элемента
   * Элемент [0] = collection_date - (length - 1) дней
   * Элемент [length-1] = collection_date
   */
  const prepareGraphData = (data: number[] | undefined, collectionDate: string | null): GraphDataPoint[] => {
    if (!data || data.length === 0 || !collectionDate) return [];
    
    const endDate = new Date(collectionDate);
    const length = data.length;
    
    return data.map((value, index) => {
      // Рассчитываем дату: конечная дата минус (длина - 1 - индекс) дней
      const date = new Date(endDate);
      date.setDate(date.getDate() - (length - 1 - index));
      
      const dateStr = date.toISOString().split('T')[0];
      const formattedDate = date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
      
      return {
        date: dateStr,
        value: value,
        formattedDate: formattedDate
      };
    });
  };

  const salesData = useMemo(() => prepareGraphData(graphsData?.sales_graph, graphsData?.collection_date || null), [graphsData?.sales_graph, graphsData?.collection_date]);
  const stocksData = useMemo(() => prepareGraphData(graphsData?.stocks_graph, graphsData?.collection_date || null), [graphsData?.stocks_graph, graphsData?.collection_date]);
  const priceData = useMemo(() => prepareGraphData(graphsData?.price_graph, graphsData?.collection_date || null), [graphsData?.price_graph, graphsData?.collection_date]);

  // Проверяем, есть ли данные для графиков
  const hasGraphData = salesData.length > 0 || stocksData.length > 0 || priceData.length > 0;
  const imageGeneration = imageGenRef.current;

  return (
    // Превью не забирает фокус: при закрытии он возвращается на фильтр сверху и страница прыгает вверх.
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="product-info-modal"
      restoreFocusOptions={{ preventScroll: true }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Информация о товаре {productId}
          {wbCardData?.imt_name && <small className="text-muted ms-2">— {wbCardData.imt_name}</small>}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '16px' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        
        {loading && !wbCardData && !graphsData ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Загрузка данных...</div>
          </div>
        ) : (
          <>
            {/* Ряд 1: Изображения (3/5) + Графики (2/5) */}
            <Row className="g-3 mb-3">
              {/* Изображения - 60% ширины */}
              <Col style={{ flex: '0 0 60%', maxWidth: '60%' }}>
                <Card className="bg-dark h-100" style={{ minHeight: '750px' }}>
                  <Card.Body className="p-2 d-flex flex-column" style={{ height: '750px' }}>
                    <div className="d-flex justify-content-end mb-2">
                      <Button
                        variant="outline-light"
                        size="sm"
                        onClick={reloadImages}
                        title="Повторить загрузку изображений"
                      >
                        Обновить фото
                      </Button>
                    </div>
                    {bigImageUrls.length > 0 ? (
                      <div className="d-flex gap-2 flex-grow-1" style={{ minHeight: 0 }}>
                        {/* Миниатюры слева с прокруткой */}
                        <div 
                          style={{ 
                            width: '80px', 
                            minWidth: '80px',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            paddingRight: '4px'
                          }}
                        >
                          {bigImageUrls.map((url, index) => (
                            <div 
                              key={`${productId}-${index}-${imageRetry}`} 
                              onClick={() => !imageErrors.has(index) && setSelectedImageIndex(index)}
                              style={{ 
                                cursor: imageErrors.has(index) ? 'default' : 'pointer',
                                border: selectedImageIndex === index ? '2px solid #0d6efd' : '2px solid transparent',
                                borderRadius: '6px',
                                padding: '2px',
                                transition: 'border-color 0.2s',
                                flexShrink: 0
                              }}
                            >
                              {!imageErrors.has(index) ? (
                                <Image
                                  src={imageSrc(url)}
                                  alt={`Миниатюра ${index + 1}`}
                                  fluid
                                  rounded
                                  style={{ 
                                    width: '100%', 
                                    height: 'auto',
                                    backgroundColor: '#2d2d2d',
                                    opacity: selectedImageIndex === index ? 1 : 0.7
                                  }}
                                  onError={(e) => {
                                    if (!e.currentTarget.isConnected) return;
                                    handleImageError(index, imageGeneration);
                                  }}
                                />
                              ) : (
                                <div 
                                  className="d-flex align-items-center justify-content-center rounded text-center"
                                  style={{ 
                                    width: '100%', 
                                    aspectRatio: '3/4',
                                    backgroundColor: '#2d2d2d',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                  }}
                                >
                                  Нет
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* Большое изображение справа */}
                        <div 
                          style={{ 
                            flex: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            overflow: 'hidden'
                          }}
                        >
                          {!imageErrors.has(selectedImageIndex) && bigImageUrls[selectedImageIndex] ? (
                            <Image
                              key={`${productId}-${selectedImageIndex}-${imageRetry}`}
                              src={imageSrc(bigImageUrls[selectedImageIndex])}
                              alt={`Изображение ${selectedImageIndex + 1}`}
                              fluid
                              rounded
                              style={{ 
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                backgroundColor: '#2d2d2d'
                              }}
                              onError={(e) => {
                                if (!e.currentTarget.isConnected) return;
                                handleImageError(selectedImageIndex, imageGeneration);
                              }}
                            />
                          ) : (
                            <div 
                              className="d-flex align-items-center justify-content-center rounded text-center"
                              style={{ 
                                width: '100%', 
                                height: '100%',
                                backgroundColor: '#2d2d2d',
                                color: '#6c757d',
                                fontSize: '16px'
                              }}
                            >
                              Изображение недоступно
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted text-center w-100 d-flex align-items-center justify-content-center">
                        Не удалось определить URL изображений
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Графики - 40% ширины */}
              <Col style={{ flex: '0 0 40%', maxWidth: '40%' }}>
                <Card className="bg-dark h-100" style={{ minHeight: '750px' }}>
                  <Card.Header className="py-2">
                    <h6 className="mb-0">Графики</h6>
                  </Card.Header>
                  <Card.Body className="py-2 d-flex flex-column" style={{ height: 'calc(750px - 45px)' }}>
                    {graphsLoading ? (
                      <div className="text-center py-3 flex-grow-1 d-flex align-items-center justify-content-center">
                        <div>
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Загрузка...</span>
                        </div>
                      </div>
                    ) : graphsError ? (
                      <Alert variant="warning" className="mb-0 py-2">{graphsError}</Alert>
                    ) : hasGraphData ? (
                      <div className="d-flex flex-column gap-1 flex-grow-1">
                        {/* График продаж */}
                        {salesData.length > 0 && (
                          <div style={{ flex: 1, minHeight: 0 }}>
                            <div className="text-center text-muted small" style={{ fontSize: '11px' }}>Продажи</div>
                            <ResponsiveContainer width="100%" height="90%">
                              <ComposedChart data={salesData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                <XAxis 
                                  dataKey="formattedDate" 
                                  stroke="#666" 
                                  tick={{ fill: '#666', fontSize: 8 }}
                                  interval="preserveStartEnd"
                                />
                                <YAxis 
                                  stroke="#666" 
                                  tick={{ fill: '#666', fontSize: 8 }}
                                  width={30}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: '#2d2d2d', 
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    fontSize: '11px'
                                  }}
                                  labelStyle={{ color: '#fff' }}
                                />
                                <Bar 
                                  dataKey="value" 
                                  fill="#28a74550"
                                  name="Продажи"
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="value" 
                                  name="Продажи" 
                                  stroke="#28a745" 
                                  strokeWidth={1.5}
                                  dot={false}
                                  activeDot={{ r: 3 }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* График остатков */}
                        {stocksData.length > 0 && (
                          <div style={{ flex: 1, minHeight: 0 }}>
                            <div className="text-center text-muted small" style={{ fontSize: '11px' }}>Остатки</div>
                            <ResponsiveContainer width="100%" height="90%">
                              <ComposedChart data={stocksData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                <XAxis 
                                  dataKey="formattedDate" 
                                  stroke="#666" 
                                  tick={{ fill: '#666', fontSize: 8 }}
                                  interval="preserveStartEnd"
                                />
                                <YAxis 
                                  stroke="#666" 
                                  tick={{ fill: '#666', fontSize: 8 }}
                                  width={30}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: '#2d2d2d', 
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    fontSize: '11px'
                                  }}
                                  labelStyle={{ color: '#fff' }}
                                />
                                <Bar 
                                  dataKey="value" 
                                  fill="#17a2b850"
                                  name="Остатки"
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="value" 
                                  name="Остатки" 
                                  stroke="#17a2b8" 
                                  strokeWidth={1.5}
                                  dot={false}
                                  activeDot={{ r: 3 }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* График цен */}
                        {priceData.length > 0 && (
                          <div style={{ flex: 1, minHeight: 0 }}>
                            <div className="text-center text-muted small" style={{ fontSize: '11px' }}>Цена</div>
                            <ResponsiveContainer width="100%" height="90%">
                              <ComposedChart data={priceData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                                <XAxis 
                                  dataKey="formattedDate" 
                                  stroke="#666" 
                                  tick={{ fill: '#666', fontSize: 8 }}
                                  interval="preserveStartEnd"
                                />
                                <YAxis 
                                  stroke="#666" 
                                  tick={{ fill: '#666', fontSize: 8 }}
                                  width={30}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: '#2d2d2d', 
                                    border: '1px solid #444',
                                    borderRadius: '4px',
                                    fontSize: '11px'
                                  }}
                                  labelStyle={{ color: '#fff' }}
                                  formatter={(value) => value !== undefined ? [`${value.toLocaleString('ru-RU')} ₽`, 'Цена'] : ['', 'Цена']}
                                />
                                <Bar 
                                  dataKey="value" 
                                  fill="#ffc10750"
                                  name="Цена"
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="value" 
                                  name="Цена" 
                                  stroke="#ffc107" 
                                  strokeWidth={1.5}
                                  dot={false}
                                  activeDot={{ r: 3 }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted text-center py-3 flex-grow-1 d-flex align-items-center justify-content-center">
                        Данные для графиков отсутствуют
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Ряд 2: Характеристики и описание во всю ширину */}
            <Card className="bg-dark">
              <Card.Header className="py-2">
                <h6 className="mb-0">Характеристики и описание</h6>
              </Card.Header>
              <Card.Body className="py-2">
                {wbCardLoading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Загрузка...</span>
                  </div>
                ) : wbCardError ? (
                  <Alert variant="warning" className="mb-0">{wbCardError}</Alert>
                ) : wbCardData ? (
                  <>
                    {/* Бренд */}
                    {wbCardData.selling?.brand_name && (
                      <div className="mb-2">
                        <strong>Бренд:</strong> {wbCardData.selling.brand_name}
                      </div>
                    )}

                    {/* Характеристики - вертикально */}
                    {wbCardData.grouped_options && wbCardData.grouped_options.length > 0 ? (
                      <div className="mb-3">
                        {wbCardData.grouped_options.map((group, groupIndex) => (
                          <div key={groupIndex} className="mb-3">
                            <h6 className="text-primary mb-1" style={{ fontSize: '13px' }}>{group.group_name}</h6>
                            <Table variant="dark" size="sm" bordered className="mb-0" style={{ fontSize: '12px' }}>
                              <tbody>
                                {group.options.map((option, optIndex) => (
                                  <tr key={optIndex}>
                                    <td style={{ width: '30%', color: '#adb5bd', padding: '4px 8px' }}>{option.name}</td>
                                    <td style={{ padding: '4px 8px' }}>{option.value}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ))}
                      </div>
                    ) : wbCardData.options && wbCardData.options.length > 0 ? (
                      <div className="mb-3">
                        <h6 className="text-primary mb-1" style={{ fontSize: '13px' }}>Характеристики</h6>
                        <Table variant="dark" size="sm" bordered style={{ fontSize: '12px' }}>
                          <tbody>
                            {wbCardData.options.map((option, index) => (
                              <tr key={index}>
                                <td style={{ width: '30%', color: '#adb5bd', padding: '4px 8px' }}>{option.name}</td>
                                <td style={{ padding: '4px 8px' }}>{option.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : null}

                    {/* Описание */}
                    {wbCardData.description && (
                      <div>
                        <h6 className="text-primary mb-2" style={{ fontSize: '14px' }}>Описание</h6>
                        <div 
                          className="p-2" 
                          style={{ 
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}
                        >
                          {wbCardData.description}
                        </div>
                      </div>
                    )}

                    {!wbCardData.description && !wbCardData.grouped_options?.length && !wbCardData.options?.length && (
                      <div className="text-muted">Характеристики и описание отсутствуют</div>
                    )}
                  </>
                ) : (
                  <div className="text-muted">Данные не загружены</div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ProductInfoModal;

